import React, { useEffect } from 'react';
import NextLink from 'next/link';
import { Box, Container, Typography, Button } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useStyles } from './styles';
import PropTypes from 'prop-types';
import getRedirectUrlAfterLogin from 'utils/getRedirectUrlAfterLogin';

const Props = {
  showButton: PropTypes.bool,
  bgColor: PropTypes.string,
  textButton: PropTypes.string,
  mainTitle: PropTypes.string,
  subTitle: PropTypes.string,
};

const DefaultProps = {
  showButton: true,
  textButton: 'START CREATING FOR FREE',
  mainTitle: 'How to create your Graphical Abstract in 3 steps',
  subTitle: 'We built Mind the Graph for simplicity',
  titleStepOne: <b>1. Select a template</b>,
  titleStepTwo: <b>2. Customize any element</b>,
  titleStepThree: <b>3. Download and Share</b>,
  descriptionStepOne: (
    <>
      Select from 300+ <br /> ready-to-use templates.
    </>
  ),
  descriptionStepTwo:
    'Easily select and manage illustrations, text, shapes, colors, and all other elements on your template. You can access the entire gallery and even request specific illustrations for your research.',
  descriptionStepThree:
    'You can download your creations as PNG, TIFF, and PDF files, in lower or higher quality for submitting different versions of your graphical abstracts to various journals and publications.',
  bgColor: 'common.white',
};

export default function GraphicalThreeSteps({
  showButton,
  textButton,
  mainTitle,
  subTitle,
  titleStepOne,
  titleStepTwo,
  titleStepThree,
  descriptionStepOne,
  descriptionStepTwo,
  descriptionStepThree,
  bgColor,
}) {
  const classes = useStyles();

  useEffect(() => {
    function playStepVideo(id) {
      return new Promise((resolve) => {
        const element = document.getElementById(id);

        element.load();

        element.addEventListener('loadeddata', () => {
          if (element.readyState === 4) {
            element.play();
          }
        });

        element.addEventListener('ended', () => resolve());
      });
    }

    async function handleStepVideosPlaying() {
      while (true) {
        await playStepVideo('step-video-1');

        await playStepVideo('step-video-2');

        await playStepVideo('step-video-3');
      }
    }

    handleStepVideosPlaying();
  }, []);

  return (
    <Box py={6} bgcolor={bgColor} textAlign="center">
      <Container>
        <Typography className={classes.primaryColor} variant="h2" gutterBottom={true}>
          <b>{mainTitle}</b>
        </Typography>

        <Typography>{subTitle}</Typography>

        <Box className={classes.steps}>
          <Box className={classes.stepItem}>
            <video id="step-video-1" className={classes.stepVideo} preload="metadata" muted={true}>
              <source src="/videos/graphical-abstract/step-1.webm" type="video/webm" />
              Your browser does not support HTML video.
            </video>

            <Typography className={classes.stepSubTitle} variant="h3" gutterBottom={true}>
              {titleStepOne}
            </Typography>

            <Typography>{descriptionStepOne}</Typography>
          </Box>

          <KeyboardArrowDown className={classes.stepIcon} />

          <Box className={classes.stepItem}>
            <video id="step-video-2" className={classes.stepVideo} preload="metadata" muted={true}>
              <source src="/videos/graphical-abstract/step-2.webm" type="video/webm" />
              Your browser does not support HTML video.
            </video>

            <Typography className={classes.stepSubTitle} variant="h3" gutterBottom={true}>
              {titleStepTwo}
            </Typography>

            <Typography>{descriptionStepTwo}</Typography>
          </Box>
          <KeyboardArrowDown className={classes.stepIcon} />

          <Box className={classes.stepItem}>
            <video id="step-video-3" className={classes.stepVideo} preload="metadata" muted={true}>
              <source src="/videos/graphical-abstract/step-3.webm" type="video/webm" />
              Your browser does not support HTML video.
            </video>

            <Typography className={classes.stepSubTitle} variant="h3" gutterBottom={true}>
              {titleStepThree}
            </Typography>

            <Typography>{descriptionStepThree}</Typography>
          </Box>
        </Box>

        {showButton && (
          <NextLink href={getRedirectUrlAfterLogin()} passHref={true}>
            <Button className={classes.button} color="primary" variant="contained">
              {textButton}
            </Button>
          </NextLink>
        )}
      </Container>
    </Box>
  );
}

GraphicalThreeSteps.propTypes = Props;
GraphicalThreeSteps.defaultProps = DefaultProps;
